/**
 * Iter 20.08 — Demo-Mock-Layer für axios.
 *
 * Bei aktivem Demo-Modus (`sessionStorage.et_demo_case` gesetzt) fängt dieser
 * Interceptor axios-Requests ab und gibt hart-codierte Responses zurück statt
 * echte Backend-Calls auszuführen. Realistische Latenzen simulieren echte
 * LLM/DB-Roundtrips (2-5s), damit die Demo nicht „zu schnell" wirkt.
 *
 * Aktiv gemockte Endpoints:
 *   • GET  /tool/kvs                          → DEMO_KV_LIST
 *   • POST /tool/analyze                      → case.analyzeResponse
 *   • POST /tool/extract-vordienstzeiten      → case.extractResponse
 *   • POST /tool/recalc-vordienstzeiten       → echo items unverändert
 *   • POST /tool/final-recommendation         → case.finalRecommendation
 *   • POST /tool/final-recommendation/pdf     → blockiert mit „Demo"-Marker
 *   • GET  /system/public-flags               → { registration_disabled: false }
 *   • POST /user/favorites/*                  → no-op 200
 *   • DELETE /user/favorites/*                → no-op 200
 *
 * Alle anderen Requests laufen unverändert durch (falls z.B. Auth-Kram
 * versehentlich noch aufgerufen wird, führt das zu 401 — sauberer Fail).
 */

import { DEMO_CASES, DEMO_KV_LIST } from "./cases";

const SESSION_KEY = "et_demo_case";

// Latenzen (ms) — bewusst gewählt für „realistisches" Feeling, aber ohne
// den Interessenten zu langweilen.
const LATENCY = {
  kvs: 250,
  analyze: 3200,
  extract: 4200,
  recalc: 400,
  recommend: 3800,
};

export function isDemoActive() {
  try {
    return sessionStorage.getItem(SESSION_KEY) !== null;
  } catch {
    return false;
  }
}

export function getActiveDemoCaseId() {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function setActiveDemoCase(caseId) {
  try {
    if (caseId && DEMO_CASES[caseId]) {
      sessionStorage.setItem(SESSION_KEY, caseId);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function clearDemoMode() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Fabriziert eine axios-kompatible Response.
 */
function makeResponse(config, data, status = 200) {
  return {
    data,
    status,
    statusText: "OK",
    headers: {},
    config,
    request: {},
  };
}

/**
 * Wartet die angegebene Zeit und resolved dann.
 */
function delay(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Hauptfunktion — wird von api.js als request-Interceptor registriert.
 * Wenn wir einen gemockten Endpoint erkennen, kürzen wir den Request
 * durch einen synthetischen adapter, der die pre-baked Response liefert.
 */
export function demoRequestAdapter(config) {
  const caseId = getActiveDemoCaseId();
  if (!caseId) return null;
  const kase = DEMO_CASES[caseId];
  if (!kase) return null;

  const url = String(config.url || "");
  const method = String(config.method || "get").toLowerCase();

  // GET /tool/kvs
  if (method === "get" && url.endsWith("/tool/kvs")) {
    // Der Case-KV wird als „Favorit" markiert, damit er in der KV-Grid
    // oben erscheint und der User ihn intuitiv findet.
    const items = DEMO_KV_LIST.map((kv) => ({
      ...kv,
      is_favorite: kv.id === kase.initial.kvId,
    }));
    return delay(LATENCY.kvs).then(() =>
      makeResponse(config, { items })
    );
  }

  // POST /tool/analyze
  if (method === "post" && url.endsWith("/tool/analyze")) {
    return delay(LATENCY.analyze).then(() =>
      makeResponse(config, kase.analyzeResponse)
    );
  }

  // POST /tool/extract-vordienstzeiten
  if (method === "post" && url.endsWith("/tool/extract-vordienstzeiten")) {
    return delay(LATENCY.extract).then(() =>
      makeResponse(config, kase.extractResponse)
    );
  }

  // POST /tool/recalc-vordienstzeiten — Echo der eingehenden items,
  // damit die Live-Berechnung in Step 3 nicht crasht. Aggregate-Werte
  // kommen aus dem Case, damit die „ANRECHENBAR GESAMT"-Anzeige
  // sinnvolle Zahlen zeigt.
  if (method === "post" && url.endsWith("/tool/recalc-vordienstzeiten")) {
    const body = safeParseBody(config.data);
    // Iter 20.11 — Frontend sendet Row-Array unter `rows` (nicht `vordienstzeiten`).
    // Alte Fallbacks bleiben erhalten, damit ältere Payload-Formate weiter
    // funktionieren.
    const items = Array.isArray(body?.rows)
      ? body.rows
      : Array.isArray(body?.vordienstzeiten)
        ? body.vordienstzeiten
        : [];
    const caseAggregate = kase.extractResponse?.result?.anrechenbare_monate || 0;
    return delay(LATENCY.recalc).then(() =>
      makeResponse(config, {
        ok: true,
        result: {
          items,
          zusammenfassung: kase.extractResponse?.result?.zusammenfassung || "",
          hinweise: kase.extractResponse?.result?.hinweise || [],
          anrechenbare_monate: caseAggregate,
          anrechenbare_dauer: {
            jahre: Math.floor(caseAggregate / 12),
            monate: Math.round(caseAggregate % 12),
            tage: 0,
            display: `${Math.floor(caseAggregate / 12)} J. ${Math.round(
              caseAggregate % 12
            )} M.`,
          },
          stichtag: body?.stichtag || null,
        },
      })
    );
  }

  // POST /tool/final-recommendation
  if (method === "post" && url.endsWith("/tool/final-recommendation")) {
    return delay(LATENCY.recommend).then(() =>
      makeResponse(config, kase.finalRecommendation)
    );
  }

  // POST /tool/final-recommendation/pdf — blockiert mit einer speziellen
  // Fehlermeldung, die die Frontend-Fehlerbehandlung als „Demo-Sperre"
  // darstellt. Der Text erscheint direkt im Toast — daher freundliches
  // Deutsch mit CTA-Hinweis.
  if (method === "post" && url.endsWith("/tool/final-recommendation/pdf")) {
    return delay(500).then(() => {
      const error = new Error("Demo-PDF nicht verfügbar");
      error.response = {
        status: 402,
        data: {
          detail:
            "Der PDF-Bericht ist in der Vollversion enthalten. Jetzt kostenlos testen.",
        },
      };
      error.config = config;
      throw error;
    });
  }

  // Public-Flags — für RegisterGate.
  if (method === "get" && url.endsWith("/system/public-flags")) {
    return delay(50).then(() =>
      makeResponse(config, { registration_disabled: false })
    );
  }

  // Favorites — no-op success
  if (url.includes("/user/favorites/")) {
    return delay(80).then(() => makeResponse(config, { ok: true }));
  }

  return null;
}

function safeParseBody(data) {
  if (!data) return null;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return data;
}
